import type { ReactNode } from 'react'
import {
  ENFORCEMENT_DEFAULTS,
  type EnforcementDraft,
  type EnforcementMode,
  type JsonObject,
  readEnforcement,
  watchesTerminal,
  writeEnforcement
} from './policyDocument'

interface Props {
  parameters: JsonObject
  severities: string[]
  onChange: (parameters: JsonObject) => void
  /**
   * Whether a library message supplies the words (ADR-S-053). The policy's own title and message are
   * then replaced at publication, so offering them for editing would let somebody change text no
   * device will ever show.
   */
  messageChosen?: boolean
  /** Drawn where the notification's words are chosen, so the choice sits beside what it replaces. */
  messagePicker?: ReactNode
}

const MODES: [EnforcementMode, string, string][] = [
  ['observe', 'Nothing', 'The prompt is observed and the agent takes no action on a match.'],
  [
    'record',
    'Record it',
    'A match at or above the severity below is recorded as an enforcement. Nobody on the machine is interrupted.'
  ],
  [
    'act',
    'Step in',
    'A match at or above the severity below is recorded, and the agent acts on the machine as chosen below.'
  ]
]

/**
 * What the agent does when a rule matches: the `enforcement` block.
 *
 * Before this the form had no way to write one, so a policy started from scratch could watch and
 * never stop anything, while a rule's severity hint talked about an enforcement threshold nobody
 * could set. Every value offered here is one the agent already parses, because it throws on an
 * unknown enum and that would fail the whole policy on every older agent. Keys the form does not
 * show are kept by `writeEnforcement`.
 */
export function EnforcementPanel({ parameters, severities, onChange, messageChosen = false, messagePicker }: Props) {
  const enforcement = readEnforcement(parameters)
  const terminal = watchesTerminal(parameters)
  const set = (changes: Partial<EnforcementDraft>) => onChange(writeEnforcement(parameters, { ...enforcement, ...changes }))
  // The last ticked action cannot be unticked: an empty list is refused by the agent, and quietly
  // writing a notification instead would publish something the form did not show.
  const ticked = [enforcement.blockSubmitKeys, enforcement.clearPrompt, enforcement.notifyUser].filter(Boolean).length
  const onlyOne = (on: boolean) => on && ticked === 1
  const unsanctioned = parameters.enforcement !== null && typeof parameters.enforcement === 'object' &&
    !Array.isArray(parameters.enforcement) && parameters.enforcement.appliesTo === 'unsanctioned'

  return (
    <div data-testid="enforcement-panel">
      <fieldset className="field">
        <legend>When a rule matches</legend>
        {MODES.map(([mode, label, explanation]) => {
          const refused = mode === 'act' && terminal && enforcement.mode !== 'act'
          return (
            <label key={mode} className="inline-check">
              <input
                type="radio"
                name="enforcement-mode"
                value={mode}
                checked={enforcement.mode === mode}
                disabled={refused}
                onChange={() =>
                  set(
                    mode === 'act' && enforcement.mode !== 'act'
                      ? { mode, blockSubmitKeys: true, clearPrompt: false, notifyUser: true }
                      : { mode }
                  )
                }
                data-testid={`enforcement-${mode}`}
              />
              <span>
                {label}
                <span className="hint" style={{ display: 'block' }}>
                  {explanation}
                </span>
              </span>
            </label>
          )
        })}
      </fieldset>

      {terminal && (
        <p className="hint" data-testid="enforcement-terminal">
          This assistant runs in a terminal, where the agent cannot step in safely: clearing the prompt
          cannot clear a shell line, and holding the send keys freezes the whole terminal window. Record
          it instead.
        </p>
      )}

      {enforcement.mode !== 'observe' && (
        <div className="field">
          <label htmlFor="enforcement-severity">Only for a match at or above</label>
          <select
            id="enforcement-severity"
            value={enforcement.minimumSeverity}
            onChange={(event) => set({ minimumSeverity: event.target.value })}
            data-testid="enforcement-severity"
          >
            {severities.map((severity) => (
              <option key={severity} value={severity}>
                {severity}
              </option>
            ))}
          </select>
          <div className="hint">
            Each rule names a severity. A match below this one is still observed, and nothing more. The
            bundled blocking template uses critical, so a secret or a wire instruction is stopped while a
            forecast is only recorded.
          </div>
        </div>
      )}

      {enforcement.mode === 'act' && (
        <>
          <fieldset className="field">
            <legend>What the agent does</legend>
            <label className="inline-check">
              <input
                type="checkbox"
                checked={enforcement.blockSubmitKeys}
                disabled={terminal || onlyOne(enforcement.blockSubmitKeys)}
                onChange={(event) => set({ blockSubmitKeys: event.target.checked })}
                data-testid="enforcement-block"
              />
              <span>Stop the prompt being sent</span>
            </label>
            <label className="inline-check">
              <input
                type="checkbox"
                checked={enforcement.clearPrompt}
                disabled={terminal || onlyOne(enforcement.clearPrompt)}
                onChange={(event) => set({ clearPrompt: event.target.checked })}
                data-testid="enforcement-clear"
              />
              <span>Clear what was typed</span>
            </label>
            <label className="inline-check">
              <input
                type="checkbox"
                checked={enforcement.notifyUser}
                disabled={onlyOne(enforcement.notifyUser)}
                onChange={(event) => set({ notifyUser: event.target.checked })}
                data-testid="enforcement-notify"
              />
              <span>Tell the person why</span>
            </label>
            <div className="hint">At least one has to stay ticked. To do nothing on the machine, choose Record it.</div>
          </fieldset>

          {enforcement.blockSubmitKeys && (
            <>
              <label className="inline-check">
                <input
                  type="checkbox"
                  checked={enforcement.preSubmit}
                  onChange={(event) => set({ preSubmit: event.target.checked })}
                  data-testid="enforcement-presubmit"
                />
                <span>Also check the moment somebody presses send</span>
              </label>
              <div className="hint">
                Without this the agent checks only on the interval, and somebody who types quickly can
                send between two checks. In a browser, holding a send before it goes needs both this and
                stopping the prompt being sent
                {unsanctioned
                  ? ', and this policy steps in only on unsanctioned accounts, where a browser tab never holds a send.'
                  : '.'}
              </div>

              <div className="field" style={{ marginTop: '0.85rem' }}>
                <label htmlFor="enforcement-duration">Keep sending stopped for (seconds)</label>
                <input
                  id="enforcement-duration"
                  type="number"
                  min={1}
                  max={ENFORCEMENT_DEFAULTS.maximumBlockDurationSeconds}
                  value={enforcement.blockDurationSeconds}
                  onChange={(event) =>
                    set({ blockDurationSeconds: Number.parseInt(event.target.value, 10) || 1 })
                  }
                  data-testid="enforcement-duration"
                />
                <div className="hint">From 1 to {ENFORCEMENT_DEFAULTS.maximumBlockDurationSeconds}.</div>
              </div>
            </>
          )}

          {(enforcement.notifyUser || messageChosen) && messagePicker}

          {enforcement.notifyUser && !messageChosen && (
            <>
              <div className="field">
                <label htmlFor="enforcement-title">Notification title</label>
                <input
                  id="enforcement-title"
                  value={enforcement.notificationTitle}
                  maxLength={ENFORCEMENT_DEFAULTS.maximumNotificationLength}
                  onChange={(event) => set({ notificationTitle: event.target.value })}
                  data-testid="enforcement-title"
                />
              </div>
              <div className="field">
                <label htmlFor="enforcement-message">Notification message</label>
                <input
                  id="enforcement-message"
                  value={enforcement.notificationMessage}
                  maxLength={ENFORCEMENT_DEFAULTS.maximumNotificationLength}
                  onChange={(event) => set({ notificationMessage: event.target.value })}
                  data-testid="enforcement-message"
                />
                <div className="hint">
                  {'{app}'}, {'{rules}'} and {'{severity}'} are filled in on the machine. There is no way
                  to quote what was typed, on purpose.
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* A message chosen while the policy steps in, kept visible after it stops, so the warning that
          publishing is now refused is somewhere it can be read. */}
      {enforcement.mode !== 'act' && messageChosen && messagePicker}
    </div>
  )
}
